/**
 * FingeringEditorPageDesktop (issue #17 Phase 4.1)
 *
 * PC 専用レイアウト: flex-row、 右サイドバーに EditorPanel (w-[340px])。
 * 案 C-2 採用のため、 wrapper (FingeringEditorPage) で `useFingeringEditor()`
 * を 1 度呼び出し、 結果を props `editor` で受け取る。
 *
 * Phase 4.1 commit ではテスト追加・実 wrapper 切替は次 commit (4.3) で実施。
 */
import React from 'react';
import { Link } from 'react-router-dom';
import BadgeSvg from './components/BadgeSvg';
import EditorPanel from './components/EditorPanel';
import EditorToolbar from './components/EditorToolbar';
import DiscardConfirmModal from './components/DiscardConfirmModal';
import ReauthRequiredModal from './components/ReauthRequiredModal';
import RepeatEditConfirmModal from './components/RepeatEditConfirmModal';
import ChordNotePicker from './components/ChordNotePicker';
import DragDebugOverlay from './components/editor/DragDebugOverlay';
import { dragDebug } from './components/editor/dragDebug';
import EditorHeader from './components/editor/EditorHeader';
import EditorPageNav from './components/editor/EditorPageNav';
import EditorToastBubble from './components/editor/EditorToastBubble';
import EditorSvgCanvas, { SVG_WIDTH, SVG_HEIGHT } from './components/editor/EditorSvgCanvas';
import SelectRectOverlay from './components/editor/SelectRectOverlay';
import PlacementGhost from './components/editor/PlacementGhost';
import PlacementCaptureOverlay from './components/editor/PlacementCaptureOverlay';
import { isDragTarget } from '../../utils/dragTarget';
import type { UseFingeringEditorReturn } from '../../hooks/useFingeringEditor';
import type { UseFingeringClipboardReturn } from '../../hooks/useFingeringClipboard';

interface FingeringEditorPageDesktopProps {
  editor: UseFingeringEditorReturn;
  clipboard: UseFingeringClipboardReturn;
}

const FingeringEditorPageDesktop: React.FC<FingeringEditorPageDesktopProps> = ({ editor, clipboard }) => {
  const {
    badges, fingeringData, pageBadges, chordGroups, measureLines, stats, selected, canUndo,
    // issue #166: 繰り返しグループの一時ハイライト対象 id 集合
    highlightedBadgeIds,
  } = editor.data;
  const {
    zoom, pan, zoomOrigin, isMobile, isPanDragging, currentPage,
    pageCount, pdfDataUrl, toast, loading,
  } = editor.view;
  const {
    editorMode, addMode, panelOpen,
    // issue #16 Phase 8a: 範囲選択 (Phase 3 で Core hook に追加された新フィールド)
    // v3 Phase A: selectMode toggle は撤廃済 (派生値、 Desktop hook が core 経由で直接参照)。
    selectedIds, isMultiSelect, selectRectVisible, selectedCount, storedMode,
  } = editor.mode;
  const { dragBadgeId, dragDelta } = editor.drag;
  const {
    chordPickerBeatIndex, chordPickerPos,
    detectedBpm, currentBpm, setCurrentBpm,
    tuningChangeOpen,
    // issue #166: 繰り返し編集の確認モーダル保留 state
    repeatEditConfirm,
  } = editor.edit;
  const {
    isGenerating, isGeneratingAudio,
    // issue #165: 投入前 Drive 再ログインゲート
    reauthModalOpen, handleReauthConfirm, handleReauthCancel,
  } = editor.regenerate;
  const {
    svgRef, svgWrapperRef, scrollContainerRef, dragRef, editorModeRef, clickSuppressRef,
  } = editor.refs;
  const {
    handleUndo,
    handleTouchStart, handleTouchEnd,
    dismissPanel, handlePageChange,
    handleSvgPointerDown, handleSvgClick, handleBadgeDragStart,
    handlePointerMove, handlePointerUp, handlePointerLeave,
    addNoteToBeat, handleChordNoteSelect, handleChordAddNote, handleChordPickerClose,
    handleSave, handleDelete,
    // issue #166: 繰り返し編集の確認モーダル分岐
    confirmRepeatIndividual, confirmRepeatAll,
    handleSaveAndRegenerateAll, handleGeneratePdfOnly, handleGenerateAudioOnly,
    handleTuningChangeRegenerate, handleConfirmDiscard, handleCancelDiscard,
    handleToggleAddMode, handleEditorModeChange, handleToolbarZoom,
    // issue #16 Phase 8a: 範囲選択 + 一括削除 + clear
    handleDeleteMany, clearSelection, toggle,
  } = editor.handlers;

  if (loading) {
    return <div className="fixed inset-0 bg-slate-900 flex items-center justify-center text-slate-400">読み込み中...</div>;
  }

  if (!fingeringData) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-slate-400 gap-4">
        <p>運指データがありません</p>
        <Link to="/fingering" className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800">← 運指ページに戻る</Link>
      </div>
    );
  }

  // ドラッグ中バッジの beatIndex を 1 回だけ解決する (chord-mate のドラッグ追従判定、 map 内 O(n²) 回避)。
  const dragBeatIndex = dragBadgeId !== null
    ? badges.find((x) => x.id === dragBadgeId)?.beatIndex
    : undefined;

  return (
    <div data-testid="editor-page-desktop" className="fixed inset-0 bg-slate-900 text-slate-200 flex flex-col overflow-hidden overscroll-none font-rounded">
      <EditorHeader title={fingeringData.meta.title} part={fingeringData.meta.part} mode={storedMode} stats={stats} />

      <EditorToolbar addMode={addMode} onToggleAddMode={handleToggleAddMode} zoom={zoom} onZoomChange={handleToolbarZoom}
        onSaveAndRegenerate={handleSaveAndRegenerateAll} isGenerating={isGenerating}
        isMobile={isMobile} editorMode={editorMode} onEditorModeChange={handleEditorModeChange}
        canUndo={canUndo} onUndo={handleUndo}
        currentBpm={currentBpm}
        detectedBpm={detectedBpm}
        onBpmChange={setCurrentBpm}
        bpmDirty={currentBpm !== (detectedBpm ?? 120)}
        onGeneratePdfOnly={handleGeneratePdfOnly}
        onGenerateAudioOnly={handleGenerateAudioOnly}
        isGeneratingAudio={isGeneratingAudio}
        currentScordatura={fingeringData?.meta.scordatura ?? ''}
        onTuningChangeRegenerate={handleTuningChangeRegenerate}
        // issue #16 v3 Phase A: selectMode toggle 配線は撤廃済 (Core 派生値)。 範囲選択 bridge は sticky bar のみ。
        selectedCount={selectedCount}
        onDeleteMany={() => handleDeleteMany(Array.from(selectedIds))}
        onClearSelection={clearSelection}
        // issue #168: 運指コピペを toolbar に統合 (コピー=selection bar 内 / ペースト=addButton 隣 / 配置中=インライン案内)。
        hasClipboard={clipboard.hasClipboard}
        placementActive={clipboard.placementActive}
        onCopy={clipboard.copySelection}
        onPaste={clipboard.startPlacement}
        onCancel={clipboard.cancelPlacement} />
      <DiscardConfirmModal
        isOpen={tuningChangeOpen}
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />
      <ReauthRequiredModal
        isOpen={reauthModalOpen}
        onConfirm={handleReauthConfirm}
        onCancel={handleReauthCancel}
      />
      <RepeatEditConfirmModal
        open={!!repeatEditConfirm}
        onIndividual={confirmRepeatIndividual}
        onAll={confirmRepeatAll}
      />

      <EditorPageNav currentPage={currentPage} pageCount={pageCount} onChangePage={handlePageChange} />

      {/* issue #168: 運指コピペ操作 (コピー/ペースト/配置キャンセル) は EditorToolbar に統合済 (独立バー廃止)。 */}

      <div data-testid="editor-desktop-content" className="flex-1 flex flex-row overflow-hidden relative">
        <EditorSvgCanvas
          scrollContainerRef={scrollContainerRef}
          svgWrapperRef={svgWrapperRef}
          svgRef={svgRef}
          zoom={zoom}
          pan={pan}
          zoomOrigin={zoomOrigin}
          isPanDragging={isPanDragging}
          dragBadgeId={dragBadgeId}
          addMode={addMode}
          editorMode={editorMode}
          isMobile={isMobile}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onSvgClick={handleSvgClick}
          onSvgPointerDown={handleSvgPointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onPointerCancel={(e) => {
            // TODO: REMOVE AFTER #27 DEBUG — pointercancel は本件の最重要ポイント
            dragDebug(
              `CANCEL type=${e.pointerType} phase=${dragRef.current?.phase ?? 'null'} mode=${editorModeRef.current}`,
              { highlight: true },
            );
            handlePointerLeave();
          }}
        >
          <rect width={SVG_WIDTH} height={SVG_HEIGHT} fill="#fefce8" rx="4" />
          {pdfDataUrl && (
            <image href={pdfDataUrl} x="0" y="0" width={SVG_WIDTH} height={SVG_HEIGHT} preserveAspectRatio="xMidYMid meet" />
          )}
          {measureLines.map((ml, i) => (
            <g key={`ml-${i}`}>
              <line data-testid="measure-line" data-measure-line x1={ml.x} y1={ml.y1} x2={ml.x} y2={ml.y2}
                stroke="#94a3b8" strokeWidth={0.5} strokeDasharray="3 2" opacity={0.5} />
              <text data-testid="measure-label" data-measure-label x={ml.x} y={ml.y1 - 3}
                textAnchor="middle" fontSize={6} fill="#94a3b8" opacity={0.6}>
                m{ml.measure}
              </text>
            </g>
          ))}
          {pageBadges.map((b) => {
            const chord = b.beatIndex !== null ? chordGroups.get(b.beatIndex) : undefined;
            const isDragging = dragBadgeId !== null
              && isDragTarget(b, dragBadgeId, dragBeatIndex ?? null, selectedIds);
            // issue #16 Phase 8a: selectionState 計算 (selectedIds.has + isMultiSelect の組合せ)
            const isInSelection = selectedIds.has(b.id);
            const selectionState: 'none' | 'primary' | 'multi' = isInSelection
              ? (isMultiSelect ? 'multi' : 'primary')
              : 'none';
            return <BadgeSvg key={b.id} badge={b} chordNotes={chord}
              isSelected={isInSelection}
              selectionState={selectionState}
              isHighlighted={highlightedBadgeIds.has(b.id)}
              isDragging={isDragging}
              dragDelta={isDragging ? dragDelta : null}
              applyTouchOffset={false}
              onPointerDown={(id, e) => {
                // PC mouse + Ctrl/Cmd: 追加選択 toggle (U-C)
                if (e.pointerType === 'mouse' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  e.stopPropagation();
                  toggle(id);
                  clickSuppressRef.current = true;
                  return;
                }
                handleBadgeDragStart(id, e);
              }} />;
          })}
          {/* issue #16 Phase 8a / spec §6.5: 範囲選択 drag 中の矩形 overlay (z-order 上層、 pointer-events 無効)。
              Phase H-2: SelectRectOverlay に切り出して Mobile と共通化 (DRY)。 */}
          <SelectRectOverlay selectRectVisible={selectRectVisible} currentPage={currentPage} />
          {/* issue #168: 配置モードのゴースト (SVG 子、pointer-events 無効)。 */}
          <PlacementGhost items={clipboard.ghostItems} pos={clipboard.placementActive ? clipboard.ghostPos : null} />
        </EditorSvgCanvas>

        {/* PC 専用右サイドバー (panelOpen で開閉アニメーション) */}
        <div data-testid="editor-desktop-sidebar" className={`overflow-hidden transition-[width] duration-200 ease-out flex-shrink-0 ${panelOpen ? 'w-[340px] border-l border-slate-700' : 'w-0'} bg-slate-800`}>
          <div className="w-[340px] p-5 box-border overflow-y-auto max-h-full">
            <EditorPanel
              badge={selected}
              onSave={handleSave}
              onDelete={handleDelete}
              onClose={dismissPanel}
              onAddNoteToBeat={addNoteToBeat}
              chordSizeForBeat={(bi) => chordGroups.get(bi)?.length ?? 1}
            />
          </div>
        </div>

        {/* issue #168: 配置モードの入力捕捉レイヤ (content 全域を覆う。toolbar/PageNav は覆わない)。 */}
        <PlacementCaptureOverlay
          active={clipboard.placementActive}
          onPointerMove={clipboard.handlePlacementPointerMove}
          onPointerDown={clipboard.handlePlacementPointerDown}
          onPointerUp={clipboard.handlePlacementPointerUp}
          onPointerCancel={clipboard.handlePlacementPointerCancel}
        />
      </div>

      {chordPickerBeatIndex !== null && (() => {
        const group = chordGroups.get(chordPickerBeatIndex);
        if (!group) return null;
        return (
          <ChordNotePicker
            chordNotes={group}
            position={chordPickerPos}
            onSelectNote={handleChordNoteSelect}
            onAddNote={handleChordAddNote}
            onClose={handleChordPickerClose}
            showAddOption={true}
          />
        );
      })()}

      <EditorToastBubble message={toast} />

      {/* TODO: REMOVE AFTER #27 DEBUG */}
      <DragDebugOverlay />
    </div>
  );
};

export default FingeringEditorPageDesktop;
